/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */
import { defineStore } from "pinia";
import { favoritesApi, preferencesApi } from "../AlfrescoApi.js";
import { useUserStore } from "./user.js";
import { newPristyError } from "../services/ErrorService.js";

// Module-level variable to avoid Vue reactivity on the Promise
let _loadPromise = null;

// Safety cap for the repair loop, in case an unrelated 400 keeps yielding a
// guid we cannot actually clear (avoids an infinite loop).
const MAX_REPAIR_ITERATIONS = 50;

// Node favourites are stored as comma-separated nodeRef lists in these user
// preferences. The favorites REST API resolves each entry, so an orphan (a
// deleted node) makes the whole list fail; editing the preference removes it
// without going through FavouritesService (which NPEs on a deleted target).
const FAVOURITE_CSV_PREFERENCES = [
  "org.alfresco.share.documents.favourites",
  "org.alfresco.share.folders.favourites",
];

/**
 * Remove an orphan node from the user's favourite preferences.
 * @param {string} personId
 * @param {string} guid - The guid of the orphan node
 * @returns {Promise<boolean>} true if a preference was updated, false if the
 *   guid was not found in any favourite preference
 */
async function removeOrphanFromPreferences(personId, guid) {
  for (const key of FAVOURITE_CSV_PREFERENCES) {
    let value;
    try {
      const pref = await preferencesApi.getPreference(personId, key);
      value = pref?.entry?.value;
    } catch (err) {
      if (err?.status === 404) {
        continue; // preference not set for this user
      }
      throw err;
    }
    if (typeof value === "string" && value.includes(guid)) {
      const next = value
        .split(",")
        .map((ref) => ref.trim())
        .filter((ref) => ref && !ref.includes(guid))
        .join(",");
      await preferencesApi.updatePreference(personId, key, next);
      return true;
    }
  }
  return false;
}

/**
 * Extract the guid of the offending node from an Alfresco favorites error.
 * When a favorite targets an unresolvable node (e.g. a deleted one), the list
 * endpoint returns 400 with a message such as
 * "Node is not a file: 437f154a-efaa-47fe-a029-94e7cc8c1630".
 * @param {*} err - The error thrown by favoritesApi.listFavorites
 * @returns {string|null} The node guid, or null when none can be extracted
 */
function extractOrphanGuid(err) {
  let text;
  try {
    text = [
      err?.message,
      err?.response?.text,
      typeof err?.response?.body === "string"
        ? err.response.body
        : JSON.stringify(err?.response?.body),
      JSON.stringify(err),
    ]
      .filter(Boolean)
      .join(" ");
  } catch {
    text = String(err?.message ?? err);
  }
  const match = text.match(
    /Node is not a (?:file|folder):\s*([0-9a-fA-F-]{36})/,
  );
  return match ? match[1] : null;
}

export const useFavoritesStore = defineStore({
  id: "FavoritesStore",
  state: () => ({
    favorites: [],
    loaded: false,
    loading: false,
    // True when the favorites list could not be loaded because a favorite
    // targets an unresolvable node (e.g. a deleted one). The Alfresco
    // favorites endpoint is all-or-nothing and returns 400 for the whole
    // list in that case. Callers can use this to warn the user.
    loadError: false,
    // Guid of a favorite's target node that is unresolvable (e.g. deleted) and
    // blocks the whole list. Set when such a node can be identified from the
    // 400 error; consumed by repairOrphanFavorites(). null when none is known.
    repairableGuid: null,
  }),
  getters: {
    siteFavorites: (state) => {
      return state.favorites.filter((fav) => fav.target && fav.target.site);
    },
    fileFavorites: (state) => {
      return state.favorites.filter((fav) => fav.target && fav.target.file);
    },
    folderFavorites: (state) => {
      return state.favorites.filter((fav) => fav.target && fav.target.folder);
    },
  },
  actions: {
    /**
     * Load all favorites once, with concurrent call deduplication.
     * Returns the cached favorites if already loaded, unless force is true.
     * @param {boolean} force - Force a fresh load from the API
     * @returns {Promise<Array>} The favorites array
     */
    loadFavorites(force = false) {
      if (this.loaded && !force) {
        return Promise.resolve(this.favorites);
      }

      // Deduplicate concurrent calls by returning the same Promise
      if (_loadPromise) {
        return _loadPromise;
      }

      const user = useUserStore();
      if (!user.person) {
        return Promise.resolve(this.favorites);
      }

      this.loading = true;
      this.loadError = false;
      this.repairableGuid = null;
      const opts = {
        skipCount: 0,
        maxItems: 200,
        include: ["path"],
      };

      _loadPromise = favoritesApi
        .listFavorites(user.person.id, opts)
        .then((data) => {
          this.favorites = data.list.entries.map((entry) => entry.entry);
          this.loaded = true;
          return this.favorites;
        })
        .catch((err) => {
          if (err?.status === 400) {
            // include=path can fail to resolve a deleted node's path; retry
            // without it. If that still fails, a favorite itself points to an
            // unresolvable node and the endpoint refuses the whole list.
            return favoritesApi
              .listFavorites(user.person.id, {
                skipCount: 0,
                maxItems: 200,
              })
              .then((data) => {
                this.favorites = data.list.entries.map((entry) => entry.entry);
                this.loaded = true;
                return this.favorites;
              })
              .catch((retryErr) => {
                // Degrade gracefully: resolve with an empty list and flag the
                // failure so callers (e.g. the navigation menu) keep working
                // instead of breaking entirely on a single bad favorite.
                // Keep the real cause in the console for support/diagnosis;
                // we don't surface a specific cause to the user since the 400
                // can have several origins (deleted node, unexpected type...).
                console.warn(
                  "loadFavorites: favorites list unavailable",
                  retryErr,
                );
                this.favorites = [];
                this.loaded = true;
                this.loadError = true;
                // If the offending node can be identified, expose it so the UI
                // can offer to remove the orphan favorite (repairOrphanFavorites).
                this.repairableGuid = extractOrphanGuid(retryErr);
                return this.favorites;
              });
          }
          throw newPristyError("loadFavorites", err);
        })
        .finally(() => {
          this.loading = false;
          _loadPromise = null;
        });

      return _loadPromise;
    },

    /**
     * Remove favorites whose target node no longer resolves (e.g. deleted),
     * which otherwise make the all-or-nothing list endpoint return 400.
     *
     * The endpoint stops at the first bad favorite, so orphans are discovered
     * one at a time: delete the known one, reload, and repeat while a new
     * repairable guid surfaces. Bounded by MAX_REPAIR_ITERATIONS.
     *
     * @returns {Promise<number>} The number of orphan favorites removed
     */
    async repairOrphanFavorites() {
      const user = useUserStore();
      let removed = 0;
      while (this.repairableGuid && removed < MAX_REPAIR_ITERATIONS) {
        const guid = this.repairableGuid;
        let updated;
        try {
          // The favorites REST DELETE NPEs on a deleted target, so edit the
          // underlying favourite preference directly instead.
          updated = await removeOrphanFromPreferences(user.person.id, guid);
        } catch (err) {
          console.warn("repairOrphanFavorites: preference update failed", guid, err);
          break;
        }
        if (!updated) {
          console.warn(
            "repairOrphanFavorites: guid not found in favourite preferences",
            guid,
          );
          break;
        }
        removed += 1;
        // Force a fresh load; this resets repairableGuid and sets it again only
        // if another orphan is now blocking the list.
        await this.loadFavorites(true);
      }
      if (this.repairableGuid && removed >= MAX_REPAIR_ITERATIONS) {
        console.warn(
          "repairOrphanFavorites: reached safety cap",
          MAX_REPAIR_ITERATIONS,
        );
      }
      return removed;
    },

    /**
     * Add a favorite entry to the local cache after a successful API create.
     * @param {Object} entry - The favorite entry returned by the API
     */
    addFavoriteToCache(entry) {
      this.favorites.push(entry);
    },

    /**
     * Remove a favorite from the local cache after a successful API delete.
     * @param {string} targetGuid - The GUID of the target node to remove
     */
    removeFavoriteFromCache(targetGuid) {
      this.favorites = this.favorites.filter(
        (fav) => fav.targetGuid !== targetGuid,
      );
    },

    /**
     * Reset the store to its initial state.
     */
    reset() {
      this.favorites = [];
      this.loaded = false;
      this.loading = false;
      this.loadError = false;
      this.repairableGuid = null;
      _loadPromise = null;
    },
  },
});
